import { ChainId } from '@pulsex/chains'
import { CHAINS } from './chains'

export type RpcOption = {
  label: string
  url: string
}

// Named nodes offered in the RPC selector, per chain. Order is also Auto's
// startup priority before live latency ranking kicks in, so a reliable node
// leads and cold starts don't stall on a down one.
export const RPC_OPTIONS_BY_CHAIN: Partial<Record<ChainId, RpcOption[]>> = {
  [ChainId.PULSECHAIN]: [
    { label: 'PulseChain (Official)', url: 'https://rpc.pulsechain.com' },
    { label: 'G4MM4', url: 'https://rpc-pulsechain.g4mm4.io' },
    { label: 'PublicNode', url: 'https://pulsechain-rpc.publicnode.com' },
  ],
  [ChainId.PULSECHAIN_TESTNET]: [
    { label: 'PulseChain Testnet (Official)', url: 'https://rpc.v4.testnet.pulsechain.com' },
    { label: 'G4MM4 Testnet', url: 'https://rpc-testnet-pulsechain.g4mm4.io' },
    { label: 'PublicNode Testnet', url: 'https://pulsechain-testnet-rpc.publicnode.com' },
  ],
}

export const getRpcOptions = (chainId: ChainId): RpcOption[] => RPC_OPTIONS_BY_CHAIN[chainId] ?? []

export const AUTO = 'auto'
const prefKey = (chainId: ChainId) => `pulsex.rpc.${chainId}`

// Selected RPC preference for a chain: 'auto', a preset URL, or a custom URL.
export const getRpcPreference = (chainId: ChainId): string => {
  if (typeof window === 'undefined') return AUTO
  try {
    return localStorage.getItem(prefKey(chainId)) || AUTO
  } catch {
    return AUTO
  }
}

export const setRpcPreference = (chainId: ChainId, value: string) => {
  try {
    if (!value || value === AUTO) localStorage.removeItem(prefKey(chainId))
    else localStorage.setItem(prefKey(chainId), value.trim())
  } catch {
    /* localStorage disabled */
  }
}

// URLs used to build a chain's transport, in priority order. A chain with named
// options honours the user preference; anything else uses its static default.
export const getRpcUrls = (chainId: ChainId): string[] => {
  const options = getRpcOptions(chainId)
  if (options.length === 0) {
    const chain = CHAINS.find((c) => c.id === chainId)
    return (chain?.rpcUrls.default.http as string[]) ?? []
  }
  const all = options.map((o) => o.url)
  const pref = getRpcPreference(chainId)
  if (pref === AUTO) return all
  // Selected node first, the rest kept as fallback for resilience.
  return [pref, ...all.filter((u) => u !== pref)]
}
